// @ts-check

/**
 * @file Provides shared current-user state and context hooks.
 */

import gettextConfig from "gettext-universal/build/src/config.js"
import gettextEvents from "gettext-universal/build/src/events.js"
import TranslateContext from "gettext-universal/build/src/translate-context.js"
import {createContext, memo, useContext, useEffect} from "react"
import PropTypes from "prop-types"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"

import {applyLocale, resolveStartupLocale} from "../translations/initialize.js"
import {User} from "../frontend-models/user.js"
import {expoPublicEnvFlag} from "../shared/expo-public-env.js"
import {clearSessionToken, getSessionTokenSync, getSessionUserIdSync} from "../shared/session-option.js"
import {beginReplicaBootstrap, completeReplicaBootstrap, offlineCurrentUser, sealReplica} from "../database/replica-lifecycle.js"
import {markInitialCurrentUserLoadComplete} from "./current-user-startup.js"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import {UserCurrentProfileResponse, UserUpdateProfileResponse} from "../../../shared/user-profile-command-types.js" */
/** @typedef {{id: string | number, email: string | null, first_name: string | null, last_name: string | null, name: string, admin: boolean, locale: string | null, theme?: "dark" | "light" | null, abilities?: Record<string, boolean>, primary_organization_id?: string | number | null}} CurrentUserData */
/** @typedef {{user: CurrentUserData | null, loaded: boolean, connectionError: Error | null}} CurrentUserState */
/** @typedef {{locale: string | null}} TranslateProviderState */

/** @type {Set<() => void>} */
const listeners = new Set()

/** @type {CurrentUserState} */
const shared = {connectionError: null, loaded: false, user: null}

/**
 * Generation counter for current-user state mutations. An in-flight
 * `refreshCurrentUser` call discards its response when the generation has
 * advanced, so a sign-out cannot be undone by a stale backend response.
 * @type {number}
 */
let currentUserGeneration = 0

const CurrentUserContext = createContext(/** @type {CurrentUserState} */ ({connectionError: null, loaded: false, user: null}))

/**
 * Notifies current-user subscribers.
 * @returns {void} Nothing.
 */
function emitChange() {
  if (shared.loaded) markInitialCurrentUserLoadComplete()
  for (const listener of listeners) {
    listener()
  }
}

/**
 * Syncs the gettext runtime locale to the persisted user locale and
 * mirrors the value into the local Option store so a window refresh
 * starts from the same locale instead of re-deriving from the
 * browser language. Called only on user-change events (sign-in,
 * profile refresh, local patch via `updateCurrentUser`) — never from
 * a render path — so the in-page locale state never fights with
 * renders and never mutates without a real user event.
 * @returns {void} Nothing.
 */
function syncLocaleFromCurrentUser() {
  const locale = shared.user?.locale
  if (typeof locale !== "string" || !locale) return
  if (locale === gettextConfig.getLocale()) return
  void applyLocale(locale)
}

/**
 * Surfaces a currentProfile load failure to browser error reporters.
 * @param {unknown} error Error raised while loading the current user.
 * @returns {void} Nothing.
 */
function reportLoadCurrentUserFailure(error) {
  reportCaughtStartupFailure("Failed to load current user", error)
}

/**
 * Surfaces a startup-locale resolution failure.
 * @param {unknown} error Error raised while resolving the startup locale.
 * @returns {void} Nothing.
 */
function reportStartupLocaleFailure(error) {
  reportCaughtStartupFailure("Failed to resolve startup locale", error)
}

/**
 * Reports caught startup errors without bypassing system-test retries.
 * @param {string} message Error context for logs/reporting.
 * @param {unknown} error Original caught error.
 * @returns {void} Nothing.
 */
function reportCaughtStartupFailure(message, error) {
  const wrapped = error instanceof Error ? error : new Error(`${message}: ${String(error)}`)
  if (!isSystemTestRuntime() && typeof reportError === "function") {
    reportError(wrapped)
    return
  }
  console.error(`${message}:`, error)
}

/**
 * Checks whether the browser is running under the system-test harness.
 * @returns {boolean} True for system-test URLs.
 */
function isSystemTestRuntime() {
  if (expoPublicEnvFlag("EXPO_PUBLIC_SYSTEM_TEST")) return true
  if (typeof window === "undefined" || !window.location) return false
  return new URLSearchParams(window.location.search).get("systemTest") === "true"
}

/**
 * Extracts the optional user record from a profile-style command response.
 * @template {UserCurrentProfileResponse | UserUpdateProfileResponse} Response Command response type.
 * @param {Response} response User command response payload.
 * @returns {Response["user"]} User payload, or null when signed out.
 */
export function userFromProfileCommandResponse(response) {
  return response.user
}

/**
 * Extracts the full current-user state payload from currentProfile().
 * @param {UserCurrentProfileResponse} response User.currentProfile response payload.
 * @returns {CurrentUserData | null} Current-user payload, or null when signed out.
 */
export function currentUserDataFromProfileCommandResponse(response) {
  const user = response.user
  if (!user) return null

  return {
    abilities: user.abilities,
    admin: user.admin,
    email: user.email,
    first_name: user.first_name ?? null,
    id: user.id,
    last_name: user.last_name ?? null,
    locale: user.locale ?? null,
    name: user.name,
    primary_organization_id: user.primary_organization_id,
    theme: user.theme ?? null
  }
}

/**
 * Sets the current user in shared state and notifies subscribers. Called
 * after successful sign-in.
 * @param {CurrentUserData} user User data returned by the backend.
 * @returns {void} Nothing.
 */
export function setCurrentUser(user) {
  currentUserGeneration += 1
  shared.connectionError = null
  shared.user = user
  shared.loaded = true
  syncLocaleFromCurrentUser()
  emitChange()
}

/**
 * Clears the current user from shared state. Called after sign-out.
 * @returns {void} Nothing.
 */
export function clearCurrentUser() {
  currentUserGeneration += 1
  shared.connectionError = null
  shared.user = null
  shared.loaded = true
  emitChange()
}

/**
 * Refreshes the current session by loading the backend profile and writing
 * the resolved user into shared state. Uses a generation guard so a
 * concurrent sign-out cannot be overwritten by a stale refresh response.
 * Does not flip `shared.loaded` — the boot path
 * (`WithCurrentUserComponent.initialize`) keeps the LoadingScreen up
 * until the startup locale has resolved, then sets `loaded=true`
 * itself. After the initial boot `loaded` is already true, so
 * subsequent calls from `signIn` just refresh the user.
 *
 * Applies the user's saved locale and **awaits** the persisted Option
 * write here (inline rather than via the fire-and-forget
 * `syncLocaleFromCurrentUser`) so the boot path's subsequent
 * `resolveStartupLocale` doesn't race with this save on the unique
 * `options.key` index.
 * @returns {Promise<CurrentUserData | null>} Refreshed current user.
 * @rejects {Error} When profile loading or durable replica activation fails.
 */
export async function refreshCurrentUser() {
  currentUserGeneration += 1
  const generationAtStart = currentUserGeneration

  const response = await User.currentProfile()

  if (currentUserGeneration !== generationAtStart) return shared.user

  const user = currentUserDataFromProfileCommandResponse(response)

  if (user) {
    const sessionUserId = getSessionUserIdSync()
    if (sessionUserId && sessionUserId !== String(user.id)) {
      await sealReplica()
      await clearSessionToken()
      throw new Error("Authenticated user did not match the durable session owner")
    }
    const generation = await beginReplicaBootstrap(user)
    if (currentUserGeneration !== generationAtStart) return shared.user
    await completeReplicaBootstrap({generation, ownerUserId: String(user.id)})
    if (currentUserGeneration !== generationAtStart) return shared.user
  } else if (getSessionTokenSync()) {
    await sealReplica()
    await clearSessionToken()
  }

  shared.user = user

  const locale = shared.user?.locale
  if (typeof locale === "string" && locale && locale !== gettextConfig.getLocale()) {
    await applyLocale(locale)
  }

  emitChange()

  return shared.user
}

/**
 * Returns the shared current user snapshot.
 * @returns {CurrentUserData | null} Current user data.
 */
export function getCurrentUser() {
  return shared.user
}

/**
 * Re-runs the boot-time `currentProfile` fetch after a connection
 * error. Flips `loaded` back to false so the loading screen renders
 * while the request is in flight, then either captures a fresh
 * `connectionError` or sets the resolved user.
 * @returns {Promise<void>} Resolves when the retry attempt finishes.
 */
export async function retryCurrentUser() {
  shared.connectionError = null
  shared.loaded = false
  emitChange()
  try {
    await refreshCurrentUser()
    shared.connectionError = null
  } catch (error) {
    reportLoadCurrentUserFailure(error)
    shared.connectionError = error instanceof Error ? error : new Error(String(error))
    shared.user = null
  }
  shared.loaded = true
  emitChange()
}

/**
 * Patches a subset of fields on the current user without a round trip.
 * Use this after a locally-initiated mutation that has already been
 * persisted, such as changeTheme or changeLocale, so consumers subscribed
 * via the context react immediately without refetching the full profile.
 * @param {Partial<CurrentUserData>} patch User fields to merge into shared state.
 * @returns {void} Nothing.
 */
export function updateCurrentUser(patch) {
  if (!shared.user) return
  currentUserGeneration += 1
  shared.user = {...shared.user, ...patch}
  syncLocaleFromCurrentUser()
  emitChange()
}

/**
 * @typedef {object} WithCurrentUserComponentProps
 * @property {ReactNode} [children] Wrapped app tree.
 */
/** @typedef {CurrentUserState & {locale: string | null}} WithCurrentUserComponentState */

/**
 * Provider that loads the current user once at app startup via
 * `User.currentProfile()`. Wraps the tree so every screen reads from the
 * same context instead of each issuing its own round trip.
 * @augments {ShapeComponent<WithCurrentUserComponentProps, CurrentUserState>}
 */
class WithCurrentUserComponent extends ShapeComponent {
  static propTypes = {
    children: PropTypes.any
  }

  /** @type {WithCurrentUserComponentState} */
  state = {connectionError: shared.connectionError, loaded: shared.loaded, locale: gettextConfig.getLocale() || null, user: shared.user}
  /** @type {Error | null} */
  currentUserProviderConnectionError = null
  /** @type {boolean | null} */
  currentUserProviderLoaded = null
  /** @type {CurrentUserData | null} */
  currentUserProviderUser = null
  /** @type {CurrentUserState | null} */
  currentUserProviderValue = null
  /** @type {string | null | undefined} */
  translateProviderLocale = undefined
  /** @type {TranslateProviderState | null} */
  translateProviderValue = null

  /** Subscribes the provider to current-user and gettext locale changes. */
  setup() {
    useEffect(() => {
      listeners.add(this.tt.onChange)
      gettextEvents.on("onLocaleChange", this.tt.onGettextLocaleChange)

      if (!shared.loaded) {
        void this.initialize()
      }

      return () => {
        listeners.delete(this.tt.onChange)
        gettextEvents.off("onLocaleChange", this.tt.onGettextLocaleChange)
      }
    }, [])
  }

  /**
   * Loads current-user data and resolves the startup locale.
   * @returns {Promise<void>} Resolves after initialization finishes.
   */
  async initialize() {
    try {
      await refreshCurrentUser()
      shared.connectionError = null
    } catch (error) {
      // `currentProfile` succeeds with `{user: null}` for signed-out
      // sessions, so a thrown error here means the backend is
      // unreachable (network failure, 5xx, etc.). Capture the error
      // so the boot gate can render a connection-error screen
      // instead of falling through to a sign-in form that would
      // also fail. Still surface the error through `reportError`
      // for browser error reporters; never re-throw, since the
      // useEffect call site is fire-and-forget.
      reportLoadCurrentUserFailure(error)
      const cachedUser = await offlineCurrentUser(getSessionUserIdSync())
      shared.user = cachedUser
      shared.connectionError = cachedUser ? null : (error instanceof Error ? error : new Error(String(error)))
    }

    if (!shared.user && getSessionTokenSync()) {
      await sealReplica()
      await clearSessionToken()
    }

    // Pick the boot-time locale exactly once, with priority
    // user.locale → stored Option locale → device languages → "en".
    // The LoadingScreen stays up until this resolves so consumers
    // (BoardLayout, sign-in form) render directly in the right
    // locale instead of flashing whatever the browser detected.
    // Wrapped in try/catch so a local Option read/write failure
    // (SQLite I/O error, transient storage failure) doesn't trap
    // the app on the LoadingScreen — the registered fallback
    // chain still produces readable text.
    try {
      await resolveStartupLocale(shared.user?.locale ?? null)
    } catch (error) {
      reportStartupLocaleFailure(error)
    }

    shared.loaded = true
    emitChange()
  }

  /** Copies shared current-user state into component state. */
  onChange = () => {
    this.s.connectionError = shared.connectionError
    this.s.loaded = shared.loaded
    this.s.user = shared.user
  }

  /**
   * Mirrors `gettextConfig.getLocale()` into component state whenever
   * any code calls `setLocale(...)`. The mirrored value is fed to
   * the TranslateContext provider in `render()` so `useLocale()`
   * downstream reads our chosen locale via context instead of
   * defaulting to `expo-localization` device languages — without
   * this, the first `useLocale()` call inside BoardLayout overwrites
   * the locale we just resolved with `navigator.languages[0]`.
   * @returns {void} Nothing.
   */
  onGettextLocaleChange = () => {
    this.s.locale = gettextConfig.getLocale() || null
  }

  /**
   * Renders current-user and translation context providers.
   * @returns {ReactNode} Provider tree.
   */
  render() {
    let value = this.currentUserProviderValue
    if (
      !value ||
      this.currentUserProviderConnectionError !== this.s.connectionError ||
      this.currentUserProviderLoaded !== this.s.loaded ||
      this.currentUserProviderUser !== this.s.user
    ) {
      value = {connectionError: this.s.connectionError, loaded: this.s.loaded, user: this.s.user}
      this.currentUserProviderConnectionError = this.s.connectionError
      this.currentUserProviderLoaded = this.s.loaded
      this.currentUserProviderUser = this.s.user
      this.currentUserProviderValue = value
    }

    let translateValue = this.translateProviderValue
    if (!translateValue || this.translateProviderLocale !== this.s.locale) {
      translateValue = {locale: this.s.locale}
      this.translateProviderLocale = this.s.locale
      this.translateProviderValue = translateValue
    }

    return (
      <CurrentUserContext.Provider value={value}>
        <TranslateContext.Provider value={translateValue}>
          {this.p.children}
        </TranslateContext.Provider>
      </CurrentUserContext.Provider>
    )
  }
}

/** @type {NamedExoticComponent<WithCurrentUserComponentProps>} */
export const WithCurrentUser = memo(shapeComponent(WithCurrentUserComponent))

/**
 * Reads current-user context.
 * @returns {CurrentUserState} Current user state.
 */
export default function useCurrentUser() {
  return useContext(CurrentUserContext)
}

/**
 * Reads the current user from context.
 * @returns {CurrentUserData | null} Current user data.
 */
export function useUser() {
  return useContext(CurrentUserContext).user
}
